import * as Yup from 'yup'
import {validateEIN, validatePhoneNumber, isValidSUTARate, isValidZipCode} from '../utils/EorUtils'

// EOR Profile Validation Schema
export const EorUpdateProfileValidation = Yup.object({
  legalName: Yup.string()
    .required('Legal name is required')
    .min(2, 'Legal name must be at least 2 characters')
    .max(100, 'Legal name must be less than 100 characters'),
  
  dbaName: Yup.string()
    .max(100, 'DBA name must be less than 100 characters'),
  
  ein: Yup.string()
    .required('EIN is required')
    .test('valid-ein', 'Invalid EIN format', validateEIN),
  
  address: Yup.object({
    line1: Yup.string()
      .required('Address line 1 is required')
      .max(100, 'Address line 1 must be less than 100 characters'),
    
    city: Yup.string()
      .required('City is required')
      .max(50, 'City must be less than 50 characters'),
    
    state: Yup.string()
      .required('State is required')
      .length(2, 'State must be 2 characters'),
    
    zipCode: Yup.string()
      .required('ZIP code is required')
      .test('valid-zip', 'Invalid ZIP code format', isValidZipCode),
  }),
  
  phoneNumber: Yup.string()
    .required('Phone number is required')
    .test('valid-phone', 'Invalid phone number format', validatePhoneNumber),
  
  email: Yup.string()
    .required('Email is required')
    .email('Invalid email format')
    .max(100, 'Email must be less than 100 characters'),
})

// EOR Banking Validation Schema
export const EorUpdateBankingValidation = Yup.object({
  bankName: Yup.string()
    .required('Bank name is required')
    .max(100, 'Bank name must be less than 100 characters'),
  
  accountNumber: Yup.string()
    .required('Account number is required')
    .min(4, 'Account number must be at least 4 digits')
    .max(20, 'Account number must be less than 20 digits'),
  
  routingNumber: Yup.string()
    .required('Routing number is required')
    .length(9, 'Routing number must be exactly 9 digits')
    .matches(/^\d{9}$/, 'Routing number must contain only digits'),
  
  accountType: Yup.string()
    .required('Account type is required')
    .oneOf(['Checking', 'Savings'], 'Account type must be Checking or Savings'),
  
  positivePayActivation: Yup.string()
    .oneOf(['Yes', 'No'], 'Positive pay activation must be Yes or No'),
})

// EOR Tax Compliance Validation Schema
export const EorUpdateTaxAndComplianceValidation = Yup.object({
  federalEin: Yup.string()
    .required('Federal EIN is required')
    .test('valid-ein', 'Invalid EIN format', validateEIN),
  
  stateTaxId: Yup.string()
    .max(50, 'State tax ID must be less than 50 characters'),
  
  unemploymentInsurance: Yup.string()
    .max(50, 'Unemployment insurance must be less than 50 characters'),
  
  workersCompensation: Yup.string()
    .max(50, 'Workers compensation must be less than 50 characters'),
  
  stateUiAccounts: Yup.array().of(
    Yup.object({
      state: Yup.string()
        .required('State is required')
        .length(2, 'State must be 2 characters'),
      
      uiAccountNumber: Yup.string()
        .max(50, 'UI account number must be less than 50 characters'),
      
      uiRate: Yup.string()
        .test('valid-rate', 'Invalid SUTA rate', isValidSUTARate),
      
      uiWageBase: Yup.number()
        .min(0, 'UI wage base must be positive')
        .max(999999, 'UI wage base must be less than 1,000,000'),
    })
  ),
})

// EOR Payroll Setup Validation Schema
export const EorPayrollSetupValidation = Yup.object({
  payrollCycle: Yup.string()
    .required('Payroll cycle is required')
    .oneOf(['Weekly', 'Bi-weekly', 'Semi-monthly', 'Monthly'], 'Invalid payroll cycle'),
  
  payDate: Yup.string()
    .required('Pay date is required')
    .oneOf(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'], 'Invalid pay date'),
  
  overtimeRules: Yup.string()
    .required('Overtime rules are required')
    .oneOf(['Standard', 'Alternative', 'Custom'], 'Invalid overtime rules'),
  
  payDateLag: Yup.number()
    .required('Pay date lag is required')
    .min(0, 'Pay date lag must be non-negative')
    .max(30, 'Pay date lag must be less than 30 days'),
  
  directDeposit: Yup.boolean()
    .required('Direct deposit setting is required'),
  
  payrollProcessing: Yup.boolean()
    .required('Payroll processing setting is required'),
})

// EOR File Upload Validation Schema
export const EorFileUploadValidation = Yup.object({
  file: Yup.mixed()
    .required('File is required')
    .test('file-size', 'File size must be less than 10MB', (value) => {
      if (!value) return false
      return value.size <= 10 * 1024 * 1024 // 10MB
    })
    .test('file-type', 'File must be PDF, DOC, or DOCX', (value) => {
      if (!value) return false
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
      return allowedTypes.includes(value.type)
    }),
  
  documentType: Yup.string()
    .required('Document type is required')
    .oneOf(['Business License', 'Tax Certificate', 'Insurance Certificate', 'Other'], 'Invalid document type'),
  
  description: Yup.string()
    .max(500, 'Description must be less than 500 characters'),
})
